import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  useApproveRiskAssessment,
  useCreateRiskItem,
  useDeleteRiskItem,
  useRiskAssessment,
  useSpawnProjectSnapshot,
  useUpdateRiskAssessment,
  useUpdateRiskItem,
} from '../../hooks/useRisk';
import {
  RISK_CATEGORY_OPTIONS,
  RISK_DEPT_OPTIONS,
  RISK_RATING_OPTIONS,
  RiskItem,
  RiskRating,
} from '../../services/risk.service';
import { useProjects } from '../../hooks/useProjects';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ratingScore: Record<RiskRating, number> = {
  Negligible: 1,
  Low: 2,
  Medium: 3,
  High: 4,
  'Extremely High': 5,
};

function previewRpn(l?: RiskRating, c?: RiskRating) {
  if (!l || !c) return null;
  return ratingScore[l] * ratingScore[c];
}

const RiskAssessmentWorkspacePage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: assessment, isLoading, refetch } = useRiskAssessment(id);
  const { data: projects = [] } = useProjects({ is_active: true });
  const createItem = useCreateRiskItem(id);
  const updateItem = useUpdateRiskItem(id);
  const deleteItem = useDeleteRiskItem(id);
  const updateHeader = useUpdateRiskAssessment();
  const approveMutation = useApproveRiskAssessment();
  const spawnSnapshot = useSpawnProjectSnapshot();

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editing, setEditing] = useState<RiskItem | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [itemForm] = Form.useForm();
  const [footerForm] = Form.useForm();
  const [snapshotForm] = Form.useForm();

  const editable = assessment && ['Draft', 'Under Review'].includes(assessment.status);

  const items = useMemo(() => assessment?.risk_items || [], [assessment]);

  const openCreateItem = () => {
    setEditing(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      department_code: 'QA',
      risk_category: 'Product Quality',
      likelihood: 'Medium',
      impact: 'Medium',
    });
    setItemModalOpen(true);
  };

  const openEditItem = (row: RiskItem) => {
    setEditing(row);
    itemForm.setFieldsValue(row);
    setItemModalOpen(true);
  };

  const handleItemSubmit = async (values: any) => {
    if (editing) {
      await updateItem.mutateAsync({ itemId: editing.id, data: values });
    } else {
      await createItem.mutateAsync(values);
    }
    setItemModalOpen(false);
    setEditing(null);
    itemForm.resetFields();
    refetch();
  };

  const handleSaveFooter = async () => {
    const values = await footerForm.validateFields();
    await updateHeader.mutateAsync({
      id,
      data: {
        updated_by_name: values.updated_by_name,
        updated_by_designation: values.updated_by_designation,
        register_last_updated_date: dayjs().format('YYYY-MM-DD'),
        assessment_team: values.assessment_team
          ? String(values.assessment_team)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      },
    });
    refetch();
  };

  const handleApprove = async () => {
    await approveMutation.mutateAsync({ id });
    refetch();
  };

  const handleSpawn = async (values: any) => {
    const snap = await spawnSnapshot.mutateAsync({
      assessmentId: id,
      project_id: values.project_id,
      item_ids: selectedRowKeys.length ? (selectedRowKeys as string[]) : undefined,
    });
    setSnapshotOpen(false);
    navigate(`/grc/risk-assessments/${snap.id}`);
  };

  React.useEffect(() => {
    if (assessment) {
      footerForm.setFieldsValue({
        updated_by_name: assessment.updated_by_name,
        updated_by_designation: assessment.updated_by_designation,
        assessment_team: (assessment.assessment_team || []).join(', '),
      });
    }
  }, [assessment, footerForm]);

  const likelihood = Form.useWatch('likelihood', itemForm) as RiskRating | undefined;
  const impact = Form.useWatch('impact', itemForm) as RiskRating | undefined;
  const residualL = Form.useWatch('residual_likelihood', itemForm) as RiskRating | undefined;
  const residualC = Form.useWatch('residual_impact', itemForm) as RiskRating | undefined;
  const initialRpn = previewRpn(likelihood, impact);
  const residualRpn = previewRpn(residualL, residualC);
  const contingencyLikely =
    (initialRpn != null && initialRpn >= 15) ||
    (initialRpn != null && initialRpn >= 8 && impact && ratingScore[impact] >= 4) ||
    (residualRpn != null && residualRpn >= 15);

  const levelColor = (level?: string) =>
    level === 'High' ? 'red' : level === 'Moderate' ? 'orange' : 'green';

  const columns: ColumnsType<RiskItem> = [
    { title: 'Risk #', dataIndex: 'risk_number', key: 'risk_number', width: 110, fixed: 'left' },
    { title: 'Dept', dataIndex: 'department_code', key: 'department_code', width: 70 },
    { title: 'Category', dataIndex: 'risk_category', key: 'risk_category', width: 130, ellipsis: true },
    { title: 'Description', dataIndex: 'risk_description', key: 'risk_description', width: 200, ellipsis: true },
    { title: 'Risk Effect', dataIndex: 'risk_impact', key: 'risk_impact', width: 140, ellipsis: true },
    {
      title: 'Initial',
      children: [
        { title: 'L', dataIndex: 'likelihood', key: 'likelihood', width: 100, ellipsis: true },
        { title: 'C', dataIndex: 'impact', key: 'impact', width: 100, ellipsis: true },
        {
          title: 'RPN',
          dataIndex: 'risk_score',
          key: 'risk_score',
          width: 70,
          render: (s: number, r) => <Tag color={levelColor(r.risk_level)}>{s}</Tag>,
        },
      ],
    },
    {
      title: 'Treatment',
      dataIndex: 'mitigation_actions',
      key: 'mitigation_actions',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Residual',
      children: [
        { title: 'L', dataIndex: 'residual_likelihood', key: 'residual_likelihood', width: 90, ellipsis: true },
        { title: 'C', dataIndex: 'residual_impact', key: 'residual_impact', width: 90, ellipsis: true },
        {
          title: 'RPN',
          dataIndex: 'residual_risk_score',
          key: 'residual_risk_score',
          width: 70,
          render: (s?: number, r?: RiskItem) =>
            s != null ? <Tag color={levelColor(r?.residual_risk_level)}>{s}</Tag> : '—',
        },
      ],
    },
    {
      title: 'Contingency',
      children: [
        {
          title: 'Req',
          dataIndex: 'contingency_required',
          key: 'contingency_required',
          width: 60,
          render: (v: boolean) => (v ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>),
        },
        { title: 'Plan', dataIndex: 'contingency_plan', key: 'contingency_plan', width: 140, ellipsis: true },
        {
          title: 'Responsibility',
          dataIndex: 'contingency_responsibility',
          key: 'contingency_responsibility',
          width: 120,
          ellipsis: true,
        },
        {
          title: 'Internal Comm',
          dataIndex: 'internal_communication',
          key: 'internal_communication',
          width: 120,
          ellipsis: true,
        },
        {
          title: 'External Comm',
          dataIndex: 'external_communication',
          key: 'external_communication',
          width: 120,
          ellipsis: true,
        },
      ],
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, row) => (
        <Space size="small">
          <Button type="link" size="small" disabled={!editable} onClick={() => openEditItem(row)}>
            Edit
          </Button>
          {editable && (
            <Popconfirm title="Delete this risk item?" onConfirm={() => deleteItem.mutateAsync(row.id).then(() => refetch())}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!isLoading && !assessment) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Risk assessment not found" />
        <Button style={{ marginTop: 12 }} onClick={() => navigate('/grc/risk-assessments')}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/grc/risk-assessments')}>
              Back
            </Button>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {assessment?.assessment_number || '…'}
              </Title>
              <Text type="secondary">
                {assessment?.scope} register
                {assessment?.project?.project_name ? ` · ${assessment.project.project_name}` : ''}
              </Text>
            </div>
          </Space>
          <Space wrap>
            <Button icon={<InfoCircleOutlined />} onClick={() => setHelpOpen(true)}>
              Rating guide
            </Button>
            {assessment?.scope === 'Company' && (
              <Button icon={<CopyOutlined />} onClick={() => setSnapshotOpen(true)}>
                Spawn project snapshot
              </Button>
            )}
            {editable && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateItem}>
                Add risk
              </Button>
            )}
            {assessment && !['Approved', 'Active', 'Superseded'].includes(assessment.status) && (
              <Button
                icon={<CheckCircleOutlined />}
                loading={approveMutation.isPending}
                onClick={handleApprove}
              >
                Approve
              </Button>
            )}
          </Space>
        </Space>

        <Card loading={isLoading} size="small">
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label="Status">
              <Tag>{assessment?.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Overall RPN band">
              <Tag color={levelColor(assessment?.overall_risk_level)}>{assessment?.overall_risk_level}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Avg RPN">{assessment?.overall_risk_score ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Date">
              {assessment?.assessment_date ? dayjs(assessment.assessment_date).format('DD-MMM-YYYY') : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Alert
          type="info"
          showIcon
          message="Risk Assessment and Contingency Plan Register (NTES-QSF-QA-QC-08)"
          description="RPN = Likelihood × Consequence. Contingency is required for High RPN (≥15) and for Moderate RPN with severity ≥4."
        />

        <Card title="Register lines" size="small">
          <Table
            rowKey="id"
            size="small"
            loading={isLoading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 2200 }}
            pagination={{ pageSize: 20 }}
            rowSelection={
              assessment?.scope === 'Company'
                ? { selectedRowKeys, onChange: setSelectedRowKeys }
                : undefined
            }
          />
        </Card>

        <Card title="Register footer" size="small">
          <Form form={footerForm} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="assessment_team" label="Risk Assessment Team (names)">
                  <TextArea rows={2} placeholder="Comma-separated names or IDs" disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="updated_by_name" label="Updated By (Name)">
                  <Input disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="updated_by_designation" label="Designation">
                  <Input disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Last updated">
                  <Input
                    disabled
                    value={
                      assessment?.register_last_updated_date
                        ? dayjs(assessment.register_last_updated_date).format('DD-MMM-YYYY')
                        : '—'
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            {editable && (
              <Button type="primary" onClick={handleSaveFooter} loading={updateHeader.isPending}>
                Save footer
              </Button>
            )}
          </Form>
        </Card>
      </Space>

      <Modal
        title={editing ? `Edit ${editing.risk_number}` : 'Add risk item'}
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <Form form={itemForm} layout="vertical" onFinish={handleItemSubmit}>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="department_code" label="Department" rules={[{ required: true }]}>
                <Select options={RISK_DEPT_OPTIONS.map((d) => ({ value: d, label: d }))} disabled={!!editing} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="risk_category" label="Category" rules={[{ required: true }]}>
                <Select
                  options={RISK_CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
                  showSearch
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="risk_description" label="Description of Risk" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="risk_impact" label="Risk Effect">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="likelihood" label="Initial Likelihood" rules={[{ required: true }]}>
                <Select options={RISK_RATING_OPTIONS.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="impact" label="Initial Consequence" rules={[{ required: true }]}>
                <Select options={RISK_RATING_OPTIONS.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Initial RPN">
                <Input disabled value={initialRpn ?? '—'} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="mitigation_actions"
                label="Risk Treatment / Action Description"
                rules={initialRpn != null && initialRpn >= 8 ? [{ required: true, message: 'Required when RPN ≥ 8' }] : []}
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="residual_likelihood" label="Residual Likelihood">
                <Select allowClear options={RISK_RATING_OPTIONS.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="residual_impact" label="Residual Consequence">
                <Select allowClear options={RISK_RATING_OPTIONS.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Residual RPN">
                <Input disabled value={residualRpn ?? '—'} />
              </Form.Item>
            </Col>
            {contingencyLikely && (
              <Col span={24}>
                <Alert type="warning" showIcon message="Contingency plan is required for this rating" />
              </Col>
            )}
            <Col span={12}>
              <Form.Item
                name="contingency_plan"
                label="Contingency Plan"
                rules={contingencyLikely ? [{ required: true }] : []}
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contingency_responsibility"
                label="Responsibility & Authority"
                rules={contingencyLikely ? [{ required: true }] : []}
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="internal_communication" label="Internal Communication">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="external_communication" label="External Communication">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" loading={createItem.isPending || updateItem.isPending}>
              Save
            </Button>
            <Button onClick={() => setItemModalOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Spawn project snapshot"
        open={snapshotOpen}
        onCancel={() => setSnapshotOpen(false)}
        footer={null}
      >
        <Paragraph type="secondary">
          Copies {selectedRowKeys.length ? `${selectedRowKeys.length} selected` : 'all'} register line(s) into a new
          Draft project assessment.
        </Paragraph>
        <Form form={snapshotForm} layout="vertical" onFinish={handleSpawn}>
          <Form.Item name="project_id" label="Project" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={projects.map((p: any) => ({
                value: p.id,
                label: p.project_name || p.project_code || p.id,
              }))}
            />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={spawnSnapshot.isPending}>
              Create snapshot
            </Button>
            <Button onClick={() => setSnapshotOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      <Drawer title="API Q1 Rating guide" open={helpOpen} onClose={() => setHelpOpen(false)} width={480}>
        <Title level={5}>Likelihood (Probability)</Title>
        <ul>
          <li>Negligible (1) — not in past five years</li>
          <li>Low (2) — once in past five years</li>
          <li>Medium (3) — twice in past five years</li>
          <li>High (4) — once a year in last five years</li>
          <li>Extremely High (5) — more than once a year in last three years</li>
        </ul>
        <Title level={5}>Consequence (Quality / Delivery)</Title>
        <Paragraph>
          Rate severity using the Quality or Delivery descriptions in NTES-QSF-QA-QC-08 Rating sheet (1–5:
          Negligible → Extremely High).
        </Paragraph>
        <Title level={5}>RPN bands</Title>
        <ul>
          <li>1–6 Acceptable — mitigation optional</li>
          <li>8–12 Moderate — mitigation required; contingency if severity ≥ 4</li>
          <li>15–25 High — mitigation + contingency required</li>
        </ul>
        <Paragraph type="secondary">Risk numbers use format Risk-DD-XX (QA, QC, PU, SA, MN, ST, HR, OP).</Paragraph>
      </Drawer>
    </div>
  );
};

export default RiskAssessmentWorkspacePage;
